import { ChangeDetectorRef, Component, Input, SimpleChanges } from '@angular/core';

import { trackByValue } from '@recruitee/common';

import { RtButtonVariant } from '../../common/button/button.types';
import { DialogRef } from '../../dialog/dialog-ref';
import { DialogComponentI } from '../../dialog/dialog.component';
import { RtStatefulComponent } from '../../utils/rt-stateful-component';
import { ConfirmDialogVariant } from '../dialogs.types';

export type ConfirmDialogData = {
  title: string;
  message: string;
  footerMessage: string;
  isCancelable: boolean;
  variant: string;
  safeFooter: boolean;
  checkboxes: Array<{ title: string; value?: boolean }>;
  emphasizeLastCheckbox: boolean;
  agreeLabel: string;
  cancelLabel: string;
  showMoreLabel: string;
  showLessLabel: string;
  tags: string[];
  list: string[];
  confirmActionsLayout: 'end' | 'space-between';
};

type ConfirmResult = ConfirmPayload | boolean;

interface ConfirmPayload {
  confirmed: boolean;
}

type State = {
  tmpCheckboxes: Array<{ label: string; value: boolean }>;
};

@Component({
  selector: 'rt-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.less'],
  host: {
    '[class]': 'variant',
  },
})
export class ConfirmDialogComponent
  extends RtStatefulComponent<State>
  implements DialogComponentI<ConfirmDialogData, ConfirmResult>, ConfirmDialogData
{
  @Input() public title: string;
  @Input() public message: string;
  @Input() public footerMessage: string;
  @Input() public isCancelable: boolean = true;
  @Input() public variant: ConfirmDialogVariant;
  @Input() public safeFooter: boolean = false;
  @Input() public checkboxes: Array<{ title: string; value?: boolean }> = [];
  @Input() public emphasizeLastCheckbox: boolean = true;
  @Input() public agreeLabel: string;
  @Input() public cancelLabel: string;
  @Input() public showMoreLabel: string;
  @Input() public showLessLabel: string;
  @Input() public triggerUpdateOnInit: boolean = false;
  @Input() public closePayload?: { payload: any };
  @Input() public tags: string[] = [];
  @Input() public list: string[] = [];
  @Input() public confirmActionsLayout: 'end' | 'space-between' = 'end';

  public ConfirmDialogVariant: typeof ConfirmDialogVariant = ConfirmDialogVariant;
  public visibleTags: string[] = [];
  public tagsCollapseActive: boolean = false;
  public tagsCollapsed: boolean = false;
  public trackByValue = trackByValue;

  public readonly buttonVariantMap: Record<ConfirmDialogVariant, RtButtonVariant> = {
    error: 'error',
    information: 'primary',
    success: 'success',
    warning: 'warning',
  };

  private readonly TAGS_LIMIT: number = 9;
  constructor(
    private dialogRef: DialogRef<ConfirmDialogComponent, ConfirmResult>,
    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.cdRef.detach();
    this.createState({ tmpCheckboxes: [] });
  }

  public setDialogData(data: ConfirmDialogData, triggerUpdate: boolean): void {
    Object.keys(data).forEach(k => {
      // @ts-ignore
      this[k] = data[k];
    });

    if (this.tags) {
      this.prepareTagsToDisplay(this.tags);
    }

    if (triggerUpdate) {
      this.cdRef.detectChanges();
    }
  }

  public ngOnInit(): void {
    this.setState({
      tmpCheckboxes: (this.checkboxes || []).map(option => ({
        label: option.title,
        value: !!option.value,
      })),
    });
    if (this.triggerUpdateOnInit) {
      this.cdRef.detectChanges();
    }
  }

  public get isFormValid(): boolean {
    return !this.state.tmpCheckboxes.some(option => !option.value);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.tags?.currentValue) {
      this.prepareTagsToDisplay(this.tags);
    }
  }

  public handleCheckboxValueChange(index: number, value: boolean): void {
    this.setState({
      tmpCheckboxes: this.state.tmpCheckboxes.map((option, i) => {
        if (i === index) {
          return { ...option, value };
        } else {
          return option;
        }
      }),
    });
  }

  public confirm(): void {
    const result = this.closePayload
      ? {
          confirmed: true,
          ...this.closePayload,
        }
      : true;
    this.dialogRef.close(result);
  }

  public cancel(): void {
    const result = this.closePayload
      ? {
          confirmed: false,
          ...this.closePayload,
        }
      : false;
    this.dialogRef.close(result);
  }

  public trackByCheckbox(index: number): number {
    return index;
  }

  public toggleTagsCollapse(): void {
    this.tagsCollapsed = !this.tagsCollapsed;
    if (this.tagsCollapsed) {
      this.visibleTags = this.tags.slice(0, this.TAGS_LIMIT);
    } else {
      this.visibleTags = [...this.tags];
    }
    this.cdRef.detectChanges();
  }

  private prepareTagsToDisplay(tags: string[] = this.tags): void {
    this.visibleTags = tags.slice(0, this.TAGS_LIMIT);
    if (this.visibleTags.length < this.tags.length) {
      this.tagsCollapsed = true;
      this.tagsCollapseActive = true;
    }
  }
}
